import React, { useState, useMemo, useEffect } from 'react';

interface NumericValue {
  value: number;
  responseIndex: number;
}

interface OutlierModalProps {
  isOpen: boolean;
  onClose: () => void;
  questionId: string;
  questionTitle: string;
  numericValues: NumericValue[];
  currentExclusions: Set<number>;
  onApply: (excludedIndices: Set<number>) => void;
}

interface OutlierBounds {
  q1: number;
  q3: number;
  iqr: number;
  lower: number;
  upper: number;
}

interface Stats {
  count: number;
  average: number;
  median: number;
  stdDev: number;
  min: number;
  max: number;
}

/**
 * Calculate IQR-based outlier bounds
 */
function calculateOutlierBounds(values: number[]): OutlierBounds | null {
  if (values.length < 4) return null;

  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;

  // Calculate Q1 and Q3 using inclusive method
  const q1Index = Math.floor((n + 1) / 4) - 1;
  const q3Index = Math.floor((3 * (n + 1)) / 4) - 1;

  const q1 = sorted[Math.max(0, q1Index)];
  const q3 = sorted[Math.min(n - 1, q3Index)];
  const iqr = q3 - q1;

  return {
    q1,
    q3,
    iqr,
    lower: q1 - 1.5 * iqr,
    upper: q3 + 1.5 * iqr
  };
}

/**
 * Calculate statistics for a set of values
 */
function calculateStats(values: number[]): Stats | null {
  if (values.length === 0) return null;

  const n = values.length;
  const sum = values.reduce((a, b) => a + b, 0);
  const average = sum / n;

  const sorted = [...values].sort((a, b) => a - b);
  const median = n % 2 === 0
    ? (sorted[n / 2 - 1] + sorted[n / 2]) / 2
    : sorted[Math.floor(n / 2)];

  const variance = values.reduce((acc, val) => acc + Math.pow(val - average, 2), 0) / n;
  const stdDev = Math.sqrt(variance);

  return {
    count: n,
    average,
    median,
    stdDev,
    min: sorted[0],
    max: sorted[n - 1]
  };
}

const OutlierModal: React.FC<OutlierModalProps> = ({
  isOpen,
  onClose,
  questionTitle,
  numericValues,
  currentExclusions,
  onApply
}) => {
  // Local state for selected exclusions (before apply)
  const [selectedExclusions, setSelectedExclusions] = useState<Set<number>>(new Set(currentExclusions));

  // Reset selections when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedExclusions(new Set(currentExclusions));
    }
  }, [isOpen, currentExclusions]);

  // Extract just the values for calculations
  const allValues = useMemo(() => numericValues.map(v => v.value), [numericValues]);

  // Calculate outlier bounds
  const bounds = useMemo(() => calculateOutlierBounds(allValues), [allValues]);

  // Auto-detect outliers based on IQR
  const autoDetectedOutliers = useMemo(() => {
    if (!bounds) return new Set<number>();
    return new Set(
      numericValues
        .filter(v => v.value < bounds.lower || v.value > bounds.upper)
        .map(v => v.responseIndex)
    );
  }, [numericValues, bounds]);

  // Apply auto-detection
  const handleAutoDetect = () => {
    setSelectedExclusions(new Set(autoDetectedOutliers));
  };

  // Toggle a single value
  const toggleExclusion = (responseIndex: number) => {
    setSelectedExclusions(prev => {
      const next = new Set(prev);
      if (next.has(responseIndex)) {
        next.delete(responseIndex);
      } else {
        next.add(responseIndex);
      }
      return next;
    });
  };

  // Clear all selections
  const handleClearAll = () => {
    setSelectedExclusions(new Set());
  };

  // Calculate stats for original and filtered data
  const originalStats = useMemo(() => calculateStats(allValues), [allValues]);

  const filteredValues = useMemo(() =>
    numericValues
      .filter(v => !selectedExclusions.has(v.responseIndex))
      .map(v => v.value),
    [numericValues, selectedExclusions]
  );

  const filteredStats = useMemo(() => calculateStats(filteredValues), [filteredValues]);

  // Sort values for display (outliers first, then by value)
  const sortedValues = useMemo(() => {
    return [...numericValues].sort((a, b) => {
      const aIsOutlier = bounds && (a.value < bounds.lower || a.value > bounds.upper);
      const bIsOutlier = bounds && (b.value < bounds.lower || b.value > bounds.upper);
      if (aIsOutlier && !bIsOutlier) return -1;
      if (!aIsOutlier && bIsOutlier) return 1;
      return a.value - b.value;
    });
  }, [numericValues, bounds]);

  if (!isOpen) return null;

  const notEnoughData = numericValues.length < 4;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#1f2937', margin: 0 }}>
              Detect Outliers
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '4px 0 0 0', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {questionTitle}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: '#9ca3af'
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
          {notEnoughData ? (
            <div
              style={{
                padding: '24px',
                textAlign: 'center',
                backgroundColor: '#fef3c7',
                borderRadius: '8px',
                color: '#92400e'
              }}
            >
              <svg width="32" height="32" viewBox="0 0 20 20" fill="currentColor" style={{ margin: '0 auto 8px' }}>
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p style={{ margin: 0, fontWeight: 500 }}>Not enough data for outlier detection</p>
              <p style={{ margin: '8px 0 0 0', fontSize: '0.875rem' }}>
                At least 4 responses are required to calculate IQR bounds. You have {numericValues.length} response{numericValues.length !== 1 ? 's' : ''}.
              </p>
            </div>
          ) : (
            <>
              {/* IQR Info */}
              {bounds && (
                <div
                  style={{
                    padding: '12px 16px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '8px',
                    marginBottom: '16px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      IQR Method
                    </span>
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>|</span>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      Q1: {bounds.q1.toFixed(1)} | Q3: {bounds.q3.toFixed(1)} | IQR: {bounds.iqr.toFixed(1)}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                    <span style={{ fontWeight: 500 }}>Normal range:</span>{' '}
                    <span style={{ color: '#059669', fontWeight: 600 }}>{bounds.lower.toFixed(1)}</span>
                    {' to '}
                    <span style={{ color: '#059669', fontWeight: 600 }}>{bounds.upper.toFixed(1)}</span>
                    <span style={{ color: '#6b7280', marginLeft: '8px' }}>(values outside are outliers)</span>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button
                  onClick={handleAutoDetect}
                  style={{
                    padding: '6px 12px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    backgroundColor: autoDetectedOutliers.size > 0 ? '#8b5cf6' : '#e5e7eb',
                    color: autoDetectedOutliers.size > 0 ? 'white' : '#6b7280',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: autoDetectedOutliers.size > 0 ? 'pointer' : 'default',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  disabled={autoDetectedOutliers.size === 0}
                >
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                  </svg>
                  Auto-detect ({autoDetectedOutliers.size})
                </button>
                <button
                  onClick={handleClearAll}
                  style={{
                    padding: '6px 12px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Clear All
                </button>
              </div>

              {/* Values Table */}
              <div
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  marginBottom: '16px'
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '40px 80px 100px 1fr',
                    padding: '8px 12px',
                    backgroundColor: '#f9fafb',
                    borderBottom: '1px solid #e5e7eb',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}
                >
                  <span></span>
                  <span>Value</span>
                  <span>Response</span>
                  <span>Status</span>
                </div>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {sortedValues.map((item) => {
                    const isOutlier = bounds && (item.value < bounds.lower || item.value > bounds.upper);
                    const isSelected = selectedExclusions.has(item.responseIndex);
                    const outlierDirection = bounds
                      ? item.value < bounds.lower ? 'below' : item.value > bounds.upper ? 'above' : null
                      : null;

                    return (
                      <div
                        key={item.responseIndex}
                        onClick={() => toggleExclusion(item.responseIndex)}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '40px 80px 100px 1fr',
                          padding: '10px 12px',
                          borderBottom: '1px solid #f3f4f6',
                          cursor: 'pointer',
                          backgroundColor: isSelected ? 'rgba(220, 38, 38, 0.05)' : 'white',
                          transition: 'background-color 0.15s'
                        }}
                      >
                        <span>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            style={{ cursor: 'pointer' }}
                          />
                        </span>
                        <span style={{
                          fontWeight: 600,
                          color: isOutlier ? '#dc2626' : '#374151',
                          fontFamily: 'monospace'
                        }}>
                          {item.value}
                        </span>
                        <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                          #{item.responseIndex + 1}
                        </span>
                        <span style={{ fontSize: '0.75rem' }}>
                          {isOutlier ? (
                            <span style={{
                              color: '#dc2626',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              Outlier ({outlierDirection} Q{outlierDirection === 'below' ? '1' : '3'}±1.5×IQR)
                            </span>
                          ) : (
                            <span style={{ color: '#059669', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Normal
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Stats Comparison */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#4b5563',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '8px'
                }}>
                  Comparison
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  {/* Original Stats */}
                  <div
                    style={{
                      padding: '12px 16px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', marginBottom: '8px' }}>
                      Original (n={originalStats?.count || 0})
                    </div>
                    {originalStats && (
                      <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span>Average:</span>
                          <span style={{ fontWeight: 500 }}>{originalStats.average.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span>Median:</span>
                          <span style={{ fontWeight: 500 }}>{originalStats.median.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Std Dev:</span>
                          <span style={{ fontWeight: 500 }}>{originalStats.stdDev.toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Filtered Stats */}
                  <div
                    style={{
                      padding: '12px 16px',
                      backgroundColor: selectedExclusions.size > 0 ? '#fef2f2' : '#f9fafb',
                      borderRadius: '8px',
                      border: `1px solid ${selectedExclusions.size > 0 ? '#fecaca' : '#e5e7eb'}`
                    }}
                  >
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: selectedExclusions.size > 0 ? '#dc2626' : '#6b7280', marginBottom: '8px' }}>
                      Filtered (n={filteredStats?.count || 0})
                    </div>
                    {filteredStats ? (
                      <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span>Average:</span>
                          <span style={{ fontWeight: 500 }}>{filteredStats.average.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span>Median:</span>
                          <span style={{ fontWeight: 500 }}>{filteredStats.median.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Std Dev:</span>
                          <span style={{ fontWeight: 500 }}>{filteredStats.stdDev.toFixed(2)}</span>
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                        No data remaining
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Selection Summary */}
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                {selectedExclusions.size} value{selectedExclusions.size !== 1 ? 's' : ''} selected for exclusion
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            backgroundColor: '#f9fafb'
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              fontSize: '0.875rem',
              fontWeight: 500,
              backgroundColor: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          {!notEnoughData && (
            <button
              onClick={() => onApply(selectedExclusions)}
              style={{
                padding: '8px 16px',
                fontSize: '0.875rem',
                fontWeight: 500,
                backgroundColor: selectedExclusions.size > 0 ? '#dc2626' : '#264372',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              {selectedExclusions.size > 0
                ? `Exclude ${selectedExclusions.size} Value${selectedExclusions.size !== 1 ? 's' : ''}`
                : 'Apply (No Exclusions)'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OutlierModal;
